import { Injectable, Optional } from "@angular/core";
import { HttpBackend, HttpClient, HttpHeaders, HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from "@angular/common/http";
import { Router, RouterStateSnapshot } from "@angular/router";
import { Observable, catchError, finalize, map, of, tap } from "rxjs";
import { Store } from "@ngrx/store";
import { State } from "../store";
import { loadProfile } from "../store/profile/profile.actions";
import { logout, setSessionConfirm } from "../store/account/account.actions";
import { AlertService } from "src/app/shared/services/alert.service";
import { MessageService } from "primeng/api";
import { environment } from "../../../environments/environment";
import { MiniProfilerService } from "../services/mini-profiler.service";

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  timer;
  private expiredTokenRenewalInProgress = false;
  private renewalHttp: HttpClient | null = null;
  private readonly expiredTokenRenewalGraceMs = 60000;
  constructor(
    private store: Store<State>,
    private alertService: AlertService,
    private messageService: MessageService,
    private router: Router,
    private profiler: MiniProfilerService,
    @Optional() httpBackend?: HttpBackend
  ) {
    if (httpBackend) {
      this.renewalHttp = new HttpClient(httpBackend);
    }
  }
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let currentUser = JSON.parse(localStorage.getItem("currentUser"));
    if (currentUser && currentUser.token) {
      // add authorization header with jwt token if available
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      });
    }

    return next.handle(request).pipe(
      tap((evt) => {
        if (evt instanceof HttpResponse) {
          // update the token with the new one generatd on every request to increase the expiration to more 2 hours
          const newToken = evt.headers.get("newtoken");
          if (newToken) {
            currentUser = JSON.parse(localStorage.getItem("currentUser"));
            if (currentUser && currentUser.token !== newToken) {
              const expires = this.getTokenExpiration(evt, newToken);
              console.info(
                "JWT interceptor Refresh " + (expires ? expires.toISOString() : "unknown expiration"),
                expires,
                newToken
              );
              if (expires) {
                currentUser.expires = expires;
              }
              currentUser.token = newToken;
              localStorage.setItem("currentUser", JSON.stringify(currentUser));
            }
          }

          this.timerAutoLogout();

          if (!environment.production) {
            // https://github.com/MiniProfiler/dotnet/issues/502

            try {
              const miniProfilerIds = evt.headers.get("X-MiniProfiler-Ids");
              if (miniProfilerIds) {
                this.profiler.fetch(JSON.parse(miniProfilerIds));
              }
            } catch (err) {}
          }
        }
      })
    );
  }

  timerAutoLogout() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      var currentUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!currentUser) {
        clearTimeout(this.timer);
        return;
      }

      var expiration = new Date(currentUser.expires);
      var currentTime = new Date();
      var ctUTC = currentTime;
      var remTime = Number(expiration) - Number(ctUTC);

      var sessConfirmVisible = false;
      this.store
        .select((s) => s.accountState.sessionConfirmVisible)
        .subscribe((res) => {
          sessConfirmVisible = res;
        });
      if (!isFinite(remTime) || remTime <= 0) {
        this.renewExpiredSession(currentUser, remTime).subscribe((renewed) => {
          if (!renewed) {
            this.logoutExpiredSession(currentUser, remTime, expiration);
          }
          this.timerAutoLogout();
        });
        return;
      } else if (remTime < 300000 && sessConfirmVisible == false) {
        this.alertService
          .confirm(
            "Session Expiring",
            "You have been inactive for more than 25 minutes. Would you like to continue? If no response is given you will be logged out."
          )
          .subscribe((res) => {
            if (res) {
              this.store.dispatch(loadProfile());
              this.store.dispatch(setSessionConfirm({ isVisible: false }));
            } else {
              this.store.dispatch(logout());
              this.store.dispatch(loadProfile());
              this.store.dispatch(setSessionConfirm({ isVisible: false }));
            }
          });
        this.store.dispatch(setSessionConfirm({ isVisible: true }));
      }

      this.timerAutoLogout();
    }, 10000);
  }

  private renewExpiredSession(currentUser: any, remTime: number): Observable<boolean> {
    if (this.expiredTokenRenewalInProgress) {
      return of(true);
    }

    if (!this.canRenewExpiredSession(currentUser, remTime)) {
      return of(false);
    }

    this.expiredTokenRenewalInProgress = true;

    if (!this.renewalHttp) {
      this.store.dispatch(loadProfile());
      this.expiredTokenRenewalInProgress = false;
      return of(this.hasFutureStoredExpiration());
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${currentUser.token}`,
    });

    return this.renewalHttp
      .get(`${environment.apiServer.webApiURL}/Profile`, {
        headers,
        observe: "response",
      })
      .pipe(
        map((response) => this.applyRenewalResponse(response, currentUser)),
        catchError(() => of(false)),
        finalize(() => {
          this.expiredTokenRenewalInProgress = false;
        })
      );
  }

  private canRenewExpiredSession(currentUser: any, remTime: number): boolean {
    return !!currentUser?.token && isFinite(remTime) && remTime >= -this.expiredTokenRenewalGraceMs;
  }

  private applyRenewalResponse(response: HttpResponse<any>, currentUser: any): boolean {
    const newToken = response.headers.get("newtoken");
    if (newToken) {
      const storedUser = JSON.parse(localStorage.getItem("currentUser")) || currentUser;
      const expires = this.getTokenExpiration(response, newToken);
      storedUser.token = newToken;
      if (expires) {
        storedUser.expires = expires;
      }
      localStorage.setItem("currentUser", JSON.stringify(storedUser));
    }

    return this.hasFutureStoredExpiration();
  }

  private hasFutureStoredExpiration(): boolean {
    const storedUser = JSON.parse(localStorage.getItem("currentUser"));
    if (!storedUser?.expires) {
      return false;
    }

    const expiration = new Date(storedUser.expires);
    return isFinite(Number(expiration)) && expiration > new Date();
  }

  private logoutExpiredSession(currentUser: any, remTime: number, expiration: Date) {
    console.info(
      "JWT Interceptor Expired at " + currentUser.expires,
      remTime,
      expiration
    );
    this.store.dispatch(logout());
    var url = this.router.routerState.snapshot?.url;

    this.router.navigate(["/login"], {
      queryParams: { returnUrl: url, timeoutAt: currentUser.expires },
    });

    this.store.dispatch(setSessionConfirm({ isVisible: false }));
    this.messageService.clear("confirmToat");
    this.alertService.info("Your session expired");
  }

  private getTokenExpiration(evt: HttpResponse<any>, token: string): Date | null {
    const headerExpiration = evt.headers.get("newtokenexpires") || evt.headers.get("Expires");
    const headerDate = this.parseValidDate(headerExpiration);
    if (headerDate) {
      return headerDate;
    }

    return this.getJwtExpiration(token);
  }

  private parseValidDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  private getJwtExpiration(token: string): Date | null {
    const payloadSegment = token?.split(".")[1];
    if (!payloadSegment) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(this.toBase64(payloadSegment)));
      const expirationSeconds = Number(payload?.exp);
      if (!isFinite(expirationSeconds)) {
        return null;
      }

      return new Date(expirationSeconds * 1000);
    } catch (err) {
      return null;
    }
  }

  private toBase64(base64Url: string): string {
    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padding = base64.length % 4;
    if (padding > 0) {
      base64 += "=".repeat(4 - padding);
    }
    return base64;
  }

  parseToUTCDate(expires) {
    // Split the date and time components
    const [datePart, timePart, meridian] = expires.split(" ");

    // Split the date into month, day, and year
    const [month, day, year] = datePart.split("/").map(Number);

    // Split the time into hours, minutes, and seconds

    let [hours, minutes, seconds] = timePart.split(":").map(Number);

    // Adjust hours based on AM/PM
    if (meridian === "PM" && hours !== 12) {
      hours += 12;
    } else if (meridian === "AM" && hours === 12) {
      hours = 0;
    }

    // Create a Date object in UTC
    const utcDate = new Date(
      Date.UTC(year, month - 1, day, hours, minutes, seconds)
    );

    return utcDate;
  }
}
